import { useCallback, useEffect, useMemo, useState } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@store/helpers';
import {
  useGetMyOrganizationsQuery,
  useGetOrganizationBySlugQuery,
} from '@store/api/organizations/organizationsApi';
import { useGetOrganizationBillingPlansQuery } from '@store/api/billing/billingApi';
import { isOrgAdmin } from '@utils/access';
import { isInactiveProSubscription } from '@utils/billingInactivePro';
import { getBillingPlanDisplayName } from '@utils/billingPlans';
import { fitsProLimits } from '@utils/billingProEligibility';
import {
  dismissInactiveProResumeNudge,
  InactiveProResumeNudgeVariant,
  isInactiveProResumeNudgeDismissed,
} from '@utils/inactiveProResumeNudgeSession';
import { useGrantExpiringSoon } from './useGrantExpiringSoon';

const isGrantCurrentlyActive = (endsAt?: string | null) => {
  if (!endsAt) {
    return true;
  }

  const endsAtMs = new Date(endsAt).getTime();
  if (Number.isNaN(endsAtMs)) {
    return true;
  }

  return endsAtMs > Date.now();
};

export function useInactiveProResumeNudge(blockedByHigherPriorityUi: boolean) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAppSelector(state => state.auth.user);

  const { data: organizations } = useGetMyOrganizationsQuery();
  const orgMatch = matchPath('/organizations/:orgSlug/*', location.pathname);
  const projectMatch = matchPath('/organizations/:orgSlug/projects/:projectSlug/*', location.pathname);
  const projectAltMatch = matchPath('/projects/:projectSlug/*', location.pathname);

  const activeOrgSlug = useMemo(() => {
    const fromNestedProject = projectMatch?.params.orgSlug;
    if (fromNestedProject) {
      return fromNestedProject;
    }
    const fromOrg = orgMatch?.params.orgSlug;
    if (fromOrg) {
      return fromOrg;
    }
    const bareProjectSlug = projectAltMatch?.params.projectSlug;
    if (bareProjectSlug && user?.projects?.length) {
      const hit = user.projects.find(p => p.slug === bareProjectSlug);
      if (hit?.orgId && organizations?.length) {
        const org = organizations.find(o => o.orgId === hit.orgId);
        if (org?.slug) {
          return org.slug;
        }
      }
    }
    return organizations?.[0]?.slug;
  }, [projectMatch, orgMatch, projectAltMatch, user?.projects, organizations]);

  const isOnOrganizationBillingPage = useMemo(() => {
    const normalized = (location.pathname.replace(/\/+$/, '') || '/') as string;
    const m = matchPath('/organizations/:orgSlug/billing', normalized);
    if (!m?.params.orgSlug || !activeOrgSlug) {
      return false;
    }
    return m.params.orgSlug === activeOrgSlug;
  }, [location.pathname, activeOrgSlug]);

  const { data: organization } = useGetOrganizationBySlugQuery(activeOrgSlug || '', {
    skip: !activeOrgSlug,
  });
  const orgId = organization?.orgId;
  const isBillingAdmin = Boolean(orgId && isOrgAdmin(user, orgId));

  const {
    data: billing,
    isLoading: isBillingLoading,
    isFetching: isBillingFetching,
  } = useGetOrganizationBillingPlansQuery(orgId || '', {
    skip: !orgId || !isBillingAdmin,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  const [dismissedLocal, setDismissedLocal] = useState(false);

  const hasActivePaidSubscription = Boolean(
    billing?.subscription?.isActive &&
      billing.subscription.plan &&
      billing.subscription.plan !== 'free'
  );
  const activeGrantExpiresSoon = useGrantExpiringSoon(
    billing?.activeAccessGrant?.endsAt,
    hasActivePaidSubscription
  );
  const activeGrantEndsSoonAt =
    activeGrantExpiresSoon && billing?.activeAccessGrant?.endsAt
      ? billing.activeAccessGrant.endsAt
      : null;
  const hasCurrentlyActiveGrant = Boolean(
    billing?.activeAccessGrant && isGrantCurrentlyActive(billing.activeAccessGrant.endsAt)
  );
  const inactivePro =
    isInactiveProSubscription(billing?.subscription) && !hasCurrentlyActiveGrant;
  const proPlan = billing?.plans?.find(plan => plan.key === 'pro');
  const scheduledCancelEffectiveAt =
    billing?.subscription?.scheduledChange?.type === 'cancel' &&
    billing.subscription.scheduledChange.toPlan === 'free' &&
    billing.subscription.scheduledChange.fromPlan === 'pro_plus' &&
    billing.subscription.scheduledChange.status === 'pending' &&
    billing.subscription.plan === 'pro_plus' &&
    fitsProLimits(billing.usage, proPlan)
      ? billing.subscription.scheduledChange.effectiveAt
      : null;
  const nudgeVariant: InactiveProResumeNudgeVariant | null = activeGrantEndsSoonAt
    ? 'grant_expiring'
    : scheduledCancelEffectiveAt
      ? 'scheduled_cancel'
    : inactivePro
      ? 'inactive_pro'
      : null;

  useEffect(() => {
    if (!orgId || !nudgeVariant) {
      setDismissedLocal(false);
      return;
    }
    setDismissedLocal(isInactiveProResumeNudgeDismissed(orgId, nudgeVariant));
  }, [orgId, nudgeVariant]);

  const dismiss = useCallback(() => {
    if (!orgId || !nudgeVariant) {
      return;
    }
    dismissInactiveProResumeNudge(orgId, nudgeVariant);
    setDismissedLocal(true);
  }, [orgId, nudgeVariant]);

  const organizationLabel = organization?.name || activeOrgSlug || t('common.thisOrganization');
  const billingHref = activeOrgSlug ? `/organizations/${activeOrgSlug}/billing` : '';
  const inactivePaidPlanName = getBillingPlanDisplayName(
    billing?.subscription?.plan,
    billing?.plans
  );

  const resumePro = useCallback(async () => {
    if (!billingHref) {
      return;
    }
    navigate(billingHref);
  }, [billingHref, navigate]);

  const billingEligibleLoaded = useMemo(() => {
    if (!orgId || !isBillingAdmin) {
      return false;
    }
    if (isBillingLoading || isBillingFetching) {
      return false;
    }
    return Boolean(billing);
  }, [orgId, isBillingAdmin, isBillingLoading, isBillingFetching, billing]);

  const showHeaderBanner = useMemo(() => {
    if (blockedByHigherPriorityUi || isOnOrganizationBillingPage) {
      return false;
    }
    return billingEligibleLoaded && Boolean(nudgeVariant);
  }, [blockedByHigherPriorityUi, isOnOrganizationBillingPage, billingEligibleLoaded, nudgeVariant]);

  const open = useMemo(() => {
    if (blockedByHigherPriorityUi || isOnOrganizationBillingPage) {
      return false;
    }
    if (!orgId || !isBillingAdmin) {
      return false;
    }
    if (dismissedLocal) {
      return false;
    }
    if (isBillingLoading || isBillingFetching) {
      return false;
    }
    if (!billing) {
      return false;
    }
    return Boolean(nudgeVariant);
  }, [
    blockedByHigherPriorityUi,
    orgId,
    isBillingAdmin,
    dismissedLocal,
    isBillingLoading,
    isBillingFetching,
    billing,
    nudgeVariant,
    isOnOrganizationBillingPage,
  ]);

  return {
    open,
    dismiss,
    resumePro,
    isCheckoutLoading: false,
    organizationLabel,
    showHeaderBanner,
    billingHref,
    variant: nudgeVariant,
    grantEndsAt: activeGrantEndsSoonAt || scheduledCancelEffectiveAt,
    inactivePaidPlanName,
  };
}
